import { useCallback, useEffect, useMemo, useRef } from 'react';

import { useIsFocused } from '@react-navigation/native';
import { useRouter, useSegments } from 'expo-router';

import { useLoyaltyQuestQuery } from 'components/quests/loyalty-quest/use-loyalty-quest-query';
import { toaster } from 'components/toaster/toaster';
import { LoyaltyCmsType, LoyaltyNotificationIncentiveType } from 'generated/graphql-gateway';
import { usePrevious } from 'hooks/use-previous';
import { routes } from 'navigation/routing';
import { appendObjectToQueryString } from 'navigation/utils';
import { t } from 'state/intl/translations';
import { useLoyaltyContext } from 'state/loyalty';

import { AllowedCelebrationTypes, CelebrationConfig, NotificationType } from './types';

export const useLoyaltyCelebrations = ({
  allowedCelebrationTypes,
  disableCelebrations = false,
  onBadgeCelebration,
}: CelebrationConfig) => {
  // Used to avoid showing more than one celebration at a time
  const segments = useSegments();
  const previousSegments = usePrevious(segments);
  const hasCelebrated = useRef(false);
  const isFocused = useIsFocused();
  const { navigate } = useRouter();

  const onBadgeCelebrationRef = useRef(onBadgeCelebration);
  onBadgeCelebrationRef.current = onBadgeCelebration;

  const {
    loyaltyUserId,
    loyaltyNotifications,
    loadingNotifications,
    markLoyaltyNotificationsAsRead,
    fetchLoyaltyNotifications,
    openChallengeCompletedModal,
  } = useLoyaltyContext();

  const { loadingQuests, quest: profileQuest } = useLoyaltyQuestQuery({
    loyaltyId: loyaltyUserId,
    cmsType: LoyaltyCmsType.USER_JOURNEY,
  });

  const { loadingQuests: loadingMonthlyQuests, quest: monthlyQuest } = useLoyaltyQuestQuery({
    loyaltyId: loyaltyUserId,
  });

  const monthlyQuestId = useMemo(() => monthlyQuest?.id, [monthlyQuest]);
  const profileQuestId = useMemo(() => profileQuest?.id, [profileQuest]);
  const profileQuestSteps = useMemo(() => profileQuest?.steps, [profileQuest]);

  // Helper functions to filter notifications by type
  // CHALLENGE_COMPLETED
  const getChallengeCompletionNotifications = useCallback(
    () =>
      loyaltyNotifications?.filter(
        notification =>
          notification?.incentiveType === LoyaltyNotificationIncentiveType.QUEST &&
          notification?.notificationType === NotificationType.CHALLENGE_COMPLETED
      ) ?? [],
    [loyaltyNotifications]
  );

  // CHALLENGE_STEP_COMPLETED
  const getChallengeActivationAndStepCompletionNotifications = useCallback(() => {
    return (
      loyaltyNotifications?.filter(
        notification =>
          notification?.incentiveType === LoyaltyNotificationIncentiveType.QUEST &&
          [
            NotificationType.CHALLENGE_STEP_COMPLETED,
            NotificationType.CHALLENGED_ACTIVATED,
          ].includes(notification?.notificationType as NotificationType)
      ) ?? []
    );
  }, [loyaltyNotifications]);

  // BADGE_EARNED
  const getBadgeEarnedNotifications = useCallback(
    () =>
      loyaltyNotifications?.filter(
        notification =>
          notification?.incentiveType === LoyaltyNotificationIncentiveType.BADGE &&
          notification?.notificationType === NotificationType.BADGE_EARNED
      ) ?? [],
    [loyaltyNotifications]
  );

  // Individual celebration handlers
  const handleChallengeCompletion = useCallback(async () => {
    const challengeNotifications = getChallengeCompletionNotifications();

    if (!challengeNotifications.length) {
      return false;
    }

    const profileChallengeNotification = challengeNotifications.find(
      notification => notification?.entityId === profileQuestId
    );

    const monthlyChallengeNotification = challengeNotifications.find(notification => {
      return notification?.entityId === monthlyQuestId;
    });

    // Priority order for challenge completion celebrations:
    // 1. Combined Monthly and Profile challenges completed
    // 2. Monthly or Profile challenge completed
    const canShowCombined = allowedCelebrationTypes.includes(
      AllowedCelebrationTypes.COMBINED_CHALLENGE_COMPLETED
    );

    const hasMonthlyChallenge =
      (allowedCelebrationTypes.includes(AllowedCelebrationTypes.CHALLENGE_COMPLETED) ||
        canShowCombined) &&
      !!monthlyChallengeNotification;

    const hasProfileChallenge =
      (allowedCelebrationTypes.includes(AllowedCelebrationTypes.PROFILE_CHALLENGE_COMPLETED) ||
        canShowCombined) &&
      !!profileChallengeNotification;

    if (hasMonthlyChallenge || hasProfileChallenge) {
      const monthlyChallengePoints = monthlyQuest?.bonusPoints ?? 0;
      const profileChallengePoints = profileQuest?.steps.reduce(
        (acc, step) => acc + (step.bonusPoints ?? 0),
        0
      );

      openChallengeCompletedModal({
        ...(hasMonthlyChallenge && { monthlyChallengePoints }),
        ...(hasProfileChallenge && { profileChallengePoints }),
      });

      const challengesNotificationIds: string[] = [
        ...(hasMonthlyChallenge ? [monthlyChallengeNotification?.id ?? ''] : []),
        ...(hasProfileChallenge ? [profileChallengeNotification?.id ?? ''] : []),
      ].filter(Boolean);

      // If the user has completed the profile challenge, mark all CHALLENGE_STEP_COMPLETED
      // notifications as read, since they become redundant.
      let profileChallengeNotificationIds: string[] = [];
      if (hasProfileChallenge) {
        profileChallengeNotificationIds = getChallengeActivationAndStepCompletionNotifications()
          .filter(notification => notification?.entityId === profileQuestId)
          .map(notification => notification?.id);
      }

      await markLoyaltyNotificationsAsRead([
        ...challengesNotificationIds,
        ...profileChallengeNotificationIds,
      ]);
      return true;
    }
  }, [
    allowedCelebrationTypes,
    getChallengeActivationAndStepCompletionNotifications,
    getChallengeCompletionNotifications,
    markLoyaltyNotificationsAsRead,
    monthlyQuest?.bonusPoints,
    monthlyQuestId,
    openChallengeCompletedModal,
    profileQuest?.steps,
    profileQuestId,
  ]);

  const handleBadgeEarned = useCallback(async () => {
    const badgeNotification = getBadgeEarnedNotifications()[0];

    if (
      !badgeNotification ||
      !allowedCelebrationTypes.includes(AllowedCelebrationTypes.BADGE_EARNED)
    ) {
      return false;
    }

    await markLoyaltyNotificationsAsRead([badgeNotification.id]);
    const badgeDetailRoute = routes.badgeDetail(badgeNotification.entityId);
    navigate(appendObjectToQueryString(badgeDetailRoute, { justEarned: 'true' }));

    if (onBadgeCelebrationRef.current) {
      onBadgeCelebrationRef.current();
    }

    return true;
  }, [
    allowedCelebrationTypes,
    getBadgeEarnedNotifications,
    markLoyaltyNotificationsAsRead,
    navigate,
  ]);

  const handlePartialProfileCompletion = useCallback(async () => {
    let challengeStepNotifications = getChallengeActivationAndStepCompletionNotifications();
    // At the moment, we only support celebrations for profile challenge Activation and Updates
    challengeStepNotifications = challengeStepNotifications.filter(
      notification => notification?.entityId === profileQuestId
    );

    if (
      !challengeStepNotifications.length ||
      !allowedCelebrationTypes.includes(AllowedCelebrationTypes.PARTIAL_PROFILE_CHALLENGE_COMPLETED)
    ) {
      return false;
    }

    // Calculate the total earned points, to display all notifications at once.
    const { points, hasChallengeActivationNotification } = challengeStepNotifications.reduce(
      (acc, notification) => {
        const stepId = notification.metadata?.stepId;
        const earnedPoints = profileQuestSteps?.find(({ id }) => stepId === id)?.bonusPoints;

        return {
          points: acc.points + (earnedPoints ?? 0),
          hasChallengeActivationNotification:
            acc.hasChallengeActivationNotification ||
            notification.notificationType === NotificationType.CHALLENGED_ACTIVATED,
        };
      },
      { points: 0, hasChallengeActivationNotification: false }
    );

    // Only show toast if there are points earned
    if (points > 0) {
      const title = hasChallengeActivationNotification
        ? t('profileCompletionStarted')
        : t('profileCompletionUpdated');
      const message = t('crownsEarnedKeepGoing', { crowns: points });
      toaster.celebrationToast({ title, message, points });
    }

    await markLoyaltyNotificationsAsRead(
      challengeStepNotifications.map(notification => notification.id)
    );
    return true;
  }, [
    allowedCelebrationTypes,
    getChallengeActivationAndStepCompletionNotifications,
    markLoyaltyNotificationsAsRead,
    profileQuestId,
    profileQuestSteps,
  ]);

  const handleNotificationCelebrations = useCallback(async () => {
    if (
      !loyaltyNotifications?.length ||
      loadingNotifications ||
      loadingQuests ||
      loadingMonthlyQuests ||
      hasCelebrated.current
    ) {
      return;
    }

    hasCelebrated.current = true;

    // Try each celebration type in priority order
    const celebrationHandlers: (() => Promise<boolean>)[] = [
      handleChallengeCompletion,
      handleBadgeEarned,
      handlePartialProfileCompletion,
    ];

    for (const handler of celebrationHandlers) {
      if (await handler()) {
        break; // Only show one celebration at a time
      }
    }
  }, [
    handleBadgeEarned,
    handleChallengeCompletion,
    handlePartialProfileCompletion,
    loadingNotifications,
    loadingQuests,
    loadingMonthlyQuests,
    loyaltyNotifications?.length,
  ]);

  const recheckCelebrations = useCallback(async () => {
    if (disableCelebrations) {
      return;
    }

    await fetchLoyaltyNotifications();
    hasCelebrated.current = false;
  }, [disableCelebrations, fetchLoyaltyNotifications]);

  useEffect(() => {
    if (!isFocused || disableCelebrations) {
      hasCelebrated.current = false;
      return;
    }

    // Don't celebrate again if coming back from a modal (like celebrations or informative modals)
    if (isFocused && previousSegments?.includes('(modals)')) {
      hasCelebrated.current = true;
    }

    fetchLoyaltyNotifications();
  }, [isFocused, fetchLoyaltyNotifications, disableCelebrations, previousSegments]);

  useEffect(() => {
    if (
      !loyaltyNotifications?.length ||
      loadingNotifications ||
      loadingQuests ||
      loadingMonthlyQuests ||
      !isFocused ||
      disableCelebrations
    ) {
      return;
    }

    handleNotificationCelebrations();
  }, [
    disableCelebrations,
    handleNotificationCelebrations,
    isFocused,
    loadingMonthlyQuests,
    loadingNotifications,
    loadingQuests,
    loyaltyNotifications?.length,
  ]);

  return {
    recheckCelebrations,
  };
};
